import { useEffect, useId, useState } from 'react';

/**
 * Where to put the real artwork. Drop the supplied file here and it is used as-is,
 * everywhere, with no other change. Supply the mark and wordmark only — the tagline is
 * set in CSS beneath it, so the height prop sizes the wordmark predictably.
 *
 * <p>A JPEG carries no alpha, so whatever background it was saved on shows as a
 * rectangle wherever the logo sits on a different colour. The two places it appears are
 * the sidebar ({@code #fbfaff}) and the sign-in card ({@code #fff}), which are close
 * enough that white is invisible on both. Save on white; a PNG or SVG here would be
 * transparent and side-steps the question entirely.
 */
const ARTWORK = '/focus-logo.jpg';

// Probed once per page load and shared, so a second Logo does not re-run the check and
// flash the drawing again after the first has already settled.
let artworkState = 'unknown';
const waiting = [];

function probeArtwork(onSettled) {
  if (artworkState !== 'unknown') {
    onSettled(artworkState);
    return () => {};
  }
  let live = true;
  waiting.push((state) => live && onSettled(state));
  if (waiting.length === 1) {
    const probe = new Image();
    const settle = (state) => {
      artworkState = state;
      waiting.splice(0).forEach((notify) => notify(state));
    };
    probe.onload = () => settle('present');
    probe.onerror = () => settle('missing');
    probe.src = ARTWORK;
  }
  return () => {
    live = false;
  };
}

/**
 * The FOCUS mark: the wordmark in Fountainhead navy with the spiral standing in for
 * the O, and the tagline beneath when there is room for it.
 *
 * <p>Renders {@link ARTWORK} when that file is present, so the supplied artwork is used
 * exactly as it is. The drawing below stands in until it is — a missing file should not
 * leave a broken image on the sign-in screen.
 *
 * <p>The stand-in is drawn rather than set in a webfont, so it never flashes the wrong
 * letterforms while a font loads. Its four letters are stroked centrelines rather than
 * filled outlines: a geometric sans is constant-weight, so the stroke *is* the
 * letterform, the flat terminals come free from a butt cap, and the weight stays a
 * single number.
 *
 * <p>The drawing below is a <strong>reconstruction</strong> of the supplied artwork, not
 * the artwork itself — close, but the S terminals and the spiral's tail are visibly
 * approximate. It is the fallback: if {@code public/focus-logo.jpg} exists, that file is
 * used instead and rendered exactly as supplied. Nothing else has to change.
 */
export default function Logo({ height = 26, tagline = false, className = '' }) {
  // Two of these on one page would otherwise share gradient ids, and the second
  // would silently repaint the first.
  const uid = useId().replace(/:/g, '');
  const clip = `focus-disc-${uid}`;
  const fill = `focus-swirl-${uid}`;
  const [artwork, setArtwork] = useState(artworkState);

  useEffect(() => probeArtwork(setArtwork), []);

  return (
    <span className={`logo${className ? ` ${className}` : ''}`}>
      {artwork === 'present' ? (
        <img src={ARTWORK} alt="FOCUS" height={height} />
      ) : (
      <svg
        viewBox="0 0 456 120"
        height={height}
        width={(height * 456) / 120}
        role="img"
        aria-label="FOCUS"
        focusable="false"
      >
        <defs>
          {/* Resolved in the referencing element's user space, so it is in the
              mark's own coordinates rather than the translated ones. */}
          <clipPath id={clip}>
            <circle cx="60" cy="60" r="56" />
          </clipPath>
          <linearGradient id={fill} x1="0.12" y1="0.05" x2="0.78" y2="0.95">
            <stop offset="0" stopColor="#f2707a" />
            <stop offset="0.28" stopColor="#e5293f" />
            <stop offset="0.66" stopColor="#b31329" />
            <stop offset="1" stopColor="#780b19" />
          </linearGradient>
        </defs>

        <g fill="none" stroke="#1b3e93" strokeWidth="26" strokeLinecap="butt">
          <path d="M13 6 V106 M13 19 H72 M13 56 H62" />
          <path transform="translate(194 0)" d="M71 26 A37 37 0 1 0 71 86" />
          <path transform="translate(296 0)" d="M13 6 V68 A25 25 0 0 0 63 68 V6" />
          <path transform="translate(376 0)" d="M63 20 A26 26 0 1 0 39 58 A26 26 0 1 1 15 92" />
        </g>

        <g transform="translate(76 0)">
          <path
            clipPath={`url(#${clip})`}
            fillRule="evenodd"
            fill={`url(#${fill})`}
            d="M4.0 60.0 a56.0 56.0 0 1 0 112.00 0 a56.0 56.0 0 1 0 -112.00 0 Z M38.8 56.6 L36.6 58.6 L36.5 60.0 L36.4 61.4 L36.4 62.8 L36.4 64.3 L36.6 65.8 L36.7 67.3 L37.0 68.8 L37.3 70.3 L37.7 71.8 L38.3 73.3 L38.8 74.8 L39.5 76.3 L40.3 77.8 L41.2 79.3 L42.1 80.8 L43.2 82.2 L44.4 83.6 L45.7 84.9 L47.0 86.2 L48.5 87.5 L50.1 88.6 L51.8 89.7 L53.5 90.7 L55.4 91.7 L57.3 92.5 L59.4 93.2 L61.5 93.8 L63.7 94.3 L66.0 94.6 L68.3 94.9 L70.7 94.9 L73.1 94.9 L75.6 94.6 L78.1 94.2 L80.6 93.7 L83.1 92.9 L85.6 92.0 L88.1 90.9 L90.6 89.7 L93.1 88.2 L95.5 86.6 L97.8 84.8 L100.0 82.8 L102.2 80.6 L104.2 78.2 L106.1 75.7 L107.9 73.0 L109.6 70.1 L111.1 67.1 L112.4 64.0 L113.5 60.7 L114.4 57.3 L115.2 53.7 L115.7 50.1 L115.9 46.3 L116.0 42.5 L115.7 38.7 L115.3 34.8 L114.5 30.8 L113.5 26.9 L112.2 22.9 L110.7 19.0 L108.9 15.2 L104.5 17.3 L106.3 20.9 L107.7 24.6 L108.9 28.2 L109.8 31.9 L110.5 35.5 L111.0 39.1 L111.2 42.7 L111.1 46.2 L110.9 49.6 L110.4 52.9 L109.8 56.1 L108.9 59.3 L107.9 62.3 L106.7 65.1 L105.3 67.9 L103.9 70.5 L102.2 72.9 L100.5 75.2 L98.6 77.3 L96.7 79.3 L94.7 81.1 L92.6 82.7 L90.5 84.2 L88.3 85.5 L86.1 86.6 L83.9 87.6 L81.6 88.4 L79.4 89.0 L77.2 89.5 L75.0 89.9 L72.8 90.1 L70.7 90.1 L68.6 90.1 L66.5 89.9 L64.6 89.6 L62.7 89.2 L60.8 88.6 L59.1 88.0 L57.4 87.3 L55.8 86.5 L54.3 85.6 L52.8 84.7 L51.5 83.7 L50.2 82.6 L49.0 81.5 L48.0 80.4 L47.0 79.2 L46.1 78.0 L45.2 76.8 L44.5 75.5 L43.8 74.3 L43.3 73.0 L42.8 71.7 L42.3 70.4 L42.0 69.1 L41.7 67.8 L41.5 66.6 L41.3 65.3 L41.2 64.0 L41.2 62.8 L41.2 61.5 L41.3 60.3 L41.4 59.1 L39.6 58.6 A19.0 19.0 0 1 0 38.8 56.6 Z"
          />
        </g>
      </svg>
      )}
      {tagline && <span className="logo-tagline">Digital Learning Ecosystem</span>}
    </span>
  );
}
